/**
 * Where every surface of the app is documented (PLAN-DOCS A3, decision D-DX4).
 *
 * A docs site nobody is sent to is a docs site nobody reads. Before this the
 * whole app carried three links into `docs.beerwright.com`, all hand-written,
 * two of them pointing at a page that has since been split into five. So the
 * paths live here, once, as data: `DocsLink` renders them, and
 * `docs.test.ts` asserts that every one of them resolves to a real page in
 * `apps/docs/src/content/docs`. Renaming a docs page therefore breaks a test
 * rather than a reader.
 *
 * Keys are named for the *surface in this app*, not for the docs page, which
 * is why `batches` and `brewDay` both exist and both point at `/brew-day/`:
 * the batch list and brew-day mode are two places in the app and one page in
 * the docs, and the day the docs split them, only this file changes.
 *
 * Paths keep their trailing slash. The docs site is built with Astro's
 * `trailingSlash: 'always'`, so `/water` is a redirect and `/water/` is the
 * page; linking to the redirect costs every reader a round trip.
 */

/** The documentation site. No trailing slash — every path below starts with one. */
export const DOCS_BASE = 'https://docs.beerwright.com';

/**
 * Every documented surface, by the name the app calls it.
 *
 * `as const satisfies Record<string, string>` rather than a plain annotation:
 * it keeps the literal types, so {@link DocsKey} is the union of the keys and a
 * typo in a `docs` prop is a compile error rather than a 404.
 */
export const DOCS = {
  index: '/',
  recipeEditor: '/recipe-editor/',
  fermentables: '/fermentables/',
  hops: '/hops/',
  yeast: '/yeast/',
  misc: '/misc/',
  schedules: '/schedules/',
  water: '/water/',
  packaging: '/packaging/',
  profiles: '/profiles/',
  batches: '/brew-day/',
  brewDay: '/brew-day/',
  feedback: '/feedback/',
  devices: '/devices/',
  devicesCustom: '/devices/custom/',
  inventory: '/inventory/',
  calendar: '/calendar/',
  insights: '/insights/',
  blending: '/blending/',
  journal: '/journal/',
  gallery: '/gallery/',
  sharing: '/sharing/',
  settings: '/settings/',
  plans: '/plans/',
  assistant: '/assistant/',
  ingredients: '/ingredients/',
  styles: '/styles/',
  history: '/history/',
  brewSheet: '/brew-sheet/',
  importing: '/importing/',
  shortcuts: '/shortcuts/',
} as const satisfies Record<string, `/${string}`>;

/** One of {@link DOCS}'s keys. */
export type DocsKey = keyof typeof DOCS;

/**
 * The absolute URL of a documented surface, with an optional anchor.
 *
 * `anchor` is passed without its `#`, and is *not* encoded: docs anchors are
 * Starlight's own slugified headings, which are already URL-safe, and encoding
 * them would only turn a working link into a broken one.
 */
export function docsUrl(key: DocsKey, anchor?: string): string {
  return `${DOCS_BASE}${DOCS[key]}${anchor === undefined ? '' : `#${anchor}`}`;
}
